import { PATH } from "../models"
import { Answer, Question, Value } from "../types"

/*
 * Provenance of the anamnesis answers prefilled from the latest
 * teleconsultation.
 *
 * Keys: one per row of the antécédents line ("anamnesis_questions#illness")
 * and one per prefillable question (details screens, pregnancy).
 *
 * - `consumed`: keys the prefill must never touch again for this patient
 *   (already prefilled, answered, edited or restored).
 * - `pending`: keys prefilled on a screen the patient has not validated yet
 *   with "Suivant". Whatever the patient clicks meanwhile, their answers and
 *   the details screens a pending "Oui" inserted stay in memory only: they are
 *   stripped from the IndexedDB snapshot until the screen is validated.
 * - `touched`: keys the patient clicked. Their consumed marker (a key, never
 *   a value) is persisted even while pending, so a question he modified is
 *   not refilled from the history after a reload; untouched pending
 *   suggestions are fetched and applied again.
 */
export interface PrefillState {
  consumed: string[]
  pending: string[]
  touched: string[]
}

const ROW_SEPARATOR = "#"
const PREFILLABLE_QUESTIONS: string[] = [PATH.illness, PATH.allergy, PATH.pregnant]
// antécédents rows that insert a details screen (created with the row id)
const ROWS_WITH_DETAILS: string[] = [PATH.illness, PATH.allergy]

export const lineRowKey = (title: string): string =>
  `${PATH.anamnesis_questions}${ROW_SEPARATOR}${title}`

const isLineRowKey = (key: string): boolean =>
  key.startsWith(`${PATH.anamnesis_questions}${ROW_SEPARATOR}`)

const rowTitle = (key: string): string =>
  key.slice(PATH.anamnesis_questions.length + ROW_SEPARATOR.length)

type LineReply = { title: string; value: Value }

export const isLineReply = (value: Value): value is LineReply =>
  !!value && typeof value === "object" && "title" in (value as object)

const EMPTY: PrefillState = { consumed: [], pending: [], touched: [] }

const uniqueKeys = (keys: string[]): string[] =>
  keys.filter((key, index) => keys.indexOf(key) === index)

// Keys covered by an answer (rows for the line, the question id otherwise)
export const answerKeys = (questionId: string, values: Value[] = []): string[] => {
  if (questionId === PATH.anamnesis_questions)
    return values.filter(isLineReply).map((v) => lineRowKey(v.title))
  return PREFILLABLE_QUESTIONS.includes(questionId) ? [questionId] : []
}

export const isPrefillConsumed = (
  prefill: PrefillState | undefined,
  key: string,
): boolean => !!prefill?.consumed.includes(key)

// The prefill wrote `answer`: its keys are consumed and pending
export const markPrefilled = (
  prefill: PrefillState | undefined,
  answer: Answer,
): PrefillState => {
  const state = prefill || EMPTY
  const keys = answerKeys(answer.questionId, answer.value)
  return {
    ...state,
    consumed: uniqueKeys([...state.consumed, ...keys]),
    pending: uniqueKeys([...state.pending, ...keys]),
  }
}

// The patient clicked (selected or unselected) replies of `questionId`: the
// prefill must not touch them again. Pending keys stay pending until Next.
export const markTouched = (
  prefill: PrefillState | undefined,
  questionId: string,
  replies: { title?: unknown }[] = [],
): PrefillState | undefined => {
  const keys =
    questionId === PATH.anamnesis_questions
      ? replies.map((reply) => lineRowKey(String(reply.title)))
      : answerKeys(questionId)
  if (!keys.length) return prefill
  const state = prefill || EMPTY
  return {
    ...state,
    consumed: uniqueKeys([...state.consumed, ...keys]),
    touched: uniqueKeys([...(state.touched || []), ...keys]),
  }
}

const isKeyOf = (questionId: string) => (key: string) =>
  questionId === PATH.anamnesis_questions ? isLineRowKey(key) : key === questionId

// The patient validated (Next) or abandoned (Previous) the screen: its keys
// are no longer pending
export const settleQuestion = (
  prefill: PrefillState | undefined,
  questionId?: string,
): PrefillState | undefined => {
  if (!prefill || !questionId) return prefill
  return { ...prefill, pending: prefill.pending.filter((k) => !isKeyOf(questionId)(k)) }
}

// Answers of the pending keys of `questionId`, dropped when the screen is
// abandoned: Previous does not always clear the screen being left (e.g. when
// it skips a redirect), and an abandoned prefill must never become confirmed
export const withoutPendingAnswers = (
  answers: Answer[],
  prefill: PrefillState | undefined,
  questionId?: string,
): Answer[] => {
  const pending = (prefill?.pending || []).filter((k) =>
    questionId ? isKeyOf(questionId)(k) : false,
  )
  if (!pending.length) return answers
  return answers.flatMap((answer) => {
    if (answer.questionId !== PATH.anamnesis_questions)
      return pending.includes(answer.questionId) ? [] : [answer]
    const value = (answer.value || []).filter(
      (v) => !(isLineReply(v) && pending.includes(lineRowKey(v.title))),
    )
    return value.length ? [{ ...answer, value }] : []
  })
}

// Details screens that only exist because of a pending row of the line
export const pendingDetailsScreens = (
  prefill: PrefillState | undefined,
): string[] =>
  (prefill?.pending || [])
    .filter(isLineRowKey)
    .map(rowTitle)
    .filter((title) => ROWS_WITH_DETAILS.includes(title))

// Remove the details screens inserted by pending rows from a survey state
export const withoutPendingDetails = <
  T extends { questions?: Question[]; path?: string[] },
>(
  survey: T,
  prefill: PrefillState | undefined,
): T => {
  const ids = pendingDetailsScreens(prefill)
  if (!ids.length) return survey
  return {
    ...survey,
    questions: survey.questions?.filter((q) => !ids.includes(q.id)),
    path: survey.path?.filter((id) => !ids.includes(id)),
  }
}

// After a snapshot restore: persisted consumed keys + every restored answer
export const restorePrefillState = (
  persistedConsumed: string[] = [],
  answers: Answer[] = [],
): PrefillState => ({
  consumed: uniqueKeys([
    ...persistedConsumed,
    ...answers.flatMap((answer) => answerKeys(answer.questionId, answer.value)),
  ]),
  pending: [],
  touched: [],
})

/*
 * Survey parts safe to persist: answers of pending keys (line rows, details,
 * pregnancy) are removed, as well as the details screens inserted by pending
 * rows. Consumed keys are kept for settled and touched questions only, so an
 * untouched unconfirmed prefill is fetched and applied again after a reload.
 */
export const stripUnconfirmedPrefill = (
  survey: { questions: Question[]; path: string[]; answers: Answer[] },
  prefill: PrefillState | undefined,
): {
  questions: Question[]
  path: string[]
  answers: Answer[]
  prefillConsumed: string[]
} => {
  const pending = prefill?.pending || []
  const answers = survey.answers.flatMap((answer) => {
    if (answer.questionId !== PATH.anamnesis_questions)
      return pending.includes(answer.questionId) ? [] : [answer]
    const value = (answer.value || []).filter(
      (v) => !(isLineReply(v) && pending.includes(lineRowKey(v.title))),
    )
    if (value.length === (answer.value || []).length) return [answer]
    return value.length ? [{ ...answer, value }] : []
  })
  const { questions, path } = withoutPendingDetails(
    { questions: survey.questions, path: survey.path },
    prefill,
  )
  return {
    questions: questions || [],
    path: path || [],
    answers,
    // untouched pending suggestions are not marked, so a reload applies
    // them again; touched ones stay consumed (their values are still stripped)
    prefillConsumed: (prefill?.consumed || []).filter(
      (key) => !pending.includes(key) || (prefill?.touched || []).includes(key),
    ),
  }
}
